import React from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from 'reactstrap'
import Switch from 'react-bootstrap-switch'
import {
  FormControl,
  FormLabel,
  FormGroup as MuiFormGroup,
  Checkbox,
  FormControlLabel,
  TextField,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
  Autocomplete,
} from '@mui/material'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import moment from 'moment-timezone'
import 'moment/locale/fi'
import TinyEditor from '../Editor/TinyEditor'
import { v4 as uuidv4 } from 'uuid'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

const localizer = momentLocalizer(moment)

const DnDCalendar = withDragAndDrop(Calendar)

// Timing options for thank you messages
const BASE_OPTIONS = [
  { value: 'start', label: 'Varauksen alusta' },
  { value: 'end', label: 'Varauksen lopusta' },
  { value: 'endofday', label: 'Päivän lopussa' },
  { value: 'nextday', label: 'Seuraavana päivänä' },
]

// Returns the delay type based on base selection
function getDelayType(base) {
  switch (base) {
    case 'start':
    case 'end':
      return 'hours' // number input for hours
    case 'endofday':
      return 'none' // no delay needed
    case 'nextday':
      return 'time' // time input
    default:
      return 'hours'
  }
}

// Returns default delay value for a base
function getDefaultDelay(base) {
  switch (base) {
    case 'start':
    case 'end':
      return 1 // 1 hour
    case 'endofday':
      return 0
    case 'nextday':
      return '08:00'
    default:
      return 1
  }
}

const weekdaysAsResources = [
  { resourceId: 1, resourceTitle: 'Maanantai' },
  { resourceId: 2, resourceTitle: 'Tiistai' },
  { resourceId: 3, resourceTitle: 'Keskiviikko' },
  { resourceId: 4, resourceTitle: 'Torstai' },
  { resourceId: 5, resourceTitle: 'Perjantai' },
  { resourceId: 6, resourceTitle: 'Lauantai' },
  { resourceId: 7, resourceTitle: 'Sunnuntai' },
]

const PricingToolBar = () => {
  return <></>
}

const INIT_THANKYOU_TEMPLATE = {
  venue: 'all',
  lang: 'all',
  channel: 'mail',
  memberships: [],
  enabled: true,
  default: false,
  collection: 'reservation',
  schedule: [],
  resources: [],
  timing: {
    base: 'end',
    delay: 1,
  },
  message: {
    subject: '',
    body: '',
    htmlbody: '',
  },
}

const DEFAULT_THANKYOU_TEMPLATES = []

function formatSchedule(schedule) {
  return schedule.map((s) => {
    return {
      id: s.id,
      start: moment('1970-01-01 ' + s.start).toDate(),
      end: moment('1970-01-01 ' + s.end).toDate(),
      resourceId: s.day,
    }
  })
}

function ThankYouMessageRow(props) {
  const {
    index: i,
    template,
    openEditMessageModal,
    openEditScheduleModal,
    openEditResourcesModal,
    updateTemplateName,
    updateTemplateEnabled,
    updateBase,
    updateDelay,
    isDefault,
    isChanged,
  } = props

  const currentBase = template.timing?.base || 'end'
  const currentDelay = template.timing?.delay ?? getDefaultDelay(currentBase)
  const delayType = getDelayType(currentBase)

  const renderDelayInput = () => {
    switch (delayType) {
      case 'hours':
        return (
          <TextField
            fullWidth
            type='number'
            id={'delay.' + i}
            label='Viive (tuntia)'
            value={currentDelay}
            onChange={(e) => updateDelay(parseInt(e.target.value, 10) || 0)}
            inputProps={{ min: 0 }}
            sx={{ backgroundColor: 'white' }}
          />
        )
      case 'time':
        return (
          <TextField
            fullWidth
            type='time'
            id={'delay.' + i}
            label='Kellonaika'
            value={currentDelay}
            onChange={(e) => updateDelay(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ backgroundColor: 'white' }}
          />
        )
      case 'none':
      default:
        return (
          <TextField
            fullWidth
            id={'delay.' + i}
            label='Viive'
            value='-'
            disabled
            sx={{ backgroundColor: 'white' }}
          />
        )
    }
  }

  return (
    <Row key={i} style={{
      borderTop: '1px solid rgba(0, 0, 0, 0.10)',
      paddingTop: 15,
      paddingBottom: 15,
      alignItems: 'center',
      backgroundColor: isChanged ? 'rgba(255, 165, 0, 0.15)' : 'transparent',
    }}>
      <Col md='1' xs='12'>
        <Label for={'enabled.' + i}>Käytössä</Label>
        <div name={'enabled.' + i}>
          <Switch
            defaultValue={template.enabled || false}
            onColor='primary'
            offColor='primary'
            id={'enabled.' + i}
            name={'enabled.' + i}
            onChange={(e, s) => updateTemplateEnabled(s)}
            disabled={template.default}
          />
        </div>
      </Col>
      <Col md='3' xs='12' style={{ paddingRight: 15 }}>
        <TextField
          fullWidth
          id={'diplayName.' + i}
          label='Nimi'
          value={
            template.default
              ? 'Oletusviesti. Tarkista sisältö'
              : template.displayName
              ? template.displayName
              : ''
          }
          disabled={template.default}
          onChange={updateTemplateName}
          sx={{ backgroundColor: 'white' }}
        />
      </Col>
      <Col md='2' xs='12' style={{ paddingRight: 15 }}>
        <FormControl fullWidth sx={{ backgroundColor: 'white' }}>
          <InputLabel id={'base-label.' + i}>Ajankohta</InputLabel>
          <MuiSelect
            labelId={'base-label.' + i}
            id={'base.' + i}
            value={currentBase}
            label='Ajankohta'
            onChange={(e) => updateBase(e.target.value)}
          >
            {BASE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </MuiSelect>
        </FormControl>
      </Col>
      <Col md='2' xs='12' style={{ paddingRight: 15 }}>
        {renderDelayInput()}
      </Col>
      <Col>
        <Button color={'success'} onClick={() => openEditMessageModal(i)}>
          Viesti
        </Button>
        <Button
          disabled={isDefault}
          color={'success'}
          onClick={() => openEditScheduleModal(i)}
        >
          Aikataulu
        </Button>
        <Button
          disabled={isDefault}
          color={'success'}
          onClick={() => openEditResourcesModal(i)}
        >
          Resurssit
        </Button>
      </Col>
    </Row>
  )
}

class ThankYouMessageTemps extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      thankyou: [],
      originalThankyou: [],
      resourcecategories: [],
      resources: [],
      categoryOptions: [],
      loadingTemplates: true,
      loadingCategories: true,
      loadingResources: true,
      saved: true,
      showMessageModal: false,
      showScheduleModal: false,
      showResourcesModal: false,
      selectedIndex: -1,
      showDynamicHelp: false,
    }
  }

  componentDidMount() {
    this.unsubscribe = this.props.firebase
      .getMessageTemplates()
      .onSnapshot((doc) => {
        if (doc.exists) {
          const templates = doc.data().thankyou || DEFAULT_THANKYOU_TEMPLATES
          this.setState({
            thankyou: templates,
            originalThankyou: JSON.parse(JSON.stringify(templates)),
            loadingTemplates: false,
            saved: true,
          })
        } else {
          this.setState({
            loadingTemplates: false,
            thankyou: DEFAULT_THANKYOU_TEMPLATES,
            originalThankyou: JSON.parse(JSON.stringify(DEFAULT_THANKYOU_TEMPLATES)),
            saved: true,
          })
        }
      })
    this.unsubscribeCats = this.props.firebase
      .getResourceCategories()
      .onSnapshot((doc) => {
        if (doc.exists) {
          const resourcecategories = doc.data().categories
          const _categoryOptions = resourcecategories.map((rc) => ({
            value: rc.category.toLowerCase(),
            label: rc.category,
          }))
          _categoryOptions.push({ value: 'all', label: 'Kaikki' })

          this.setState({
            resourcecategories: doc.data().categories,
            categoryOptions: _categoryOptions,
            loadingCategories: false,
          })
        } else {
          this.setState({
            loadingCategories: false,
          })
        }
      })
    this.props.firebase.getResourses().then((_resources) => {
      const resources = _resources.docs
        .map((sh) => {
          return { ...sh.data(), id: sh.id }
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
      if (resources) {
        this.setState({
          resources: resources,
          loadingResources: false,
        })
      } else {
        this.setState({
          resources: [],
          loadingResources: false,
        })
      }
    })
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
    if (this.unsubscribeCats) {
      this.unsubscribeCats()
    }
  }

  addThankYouRow = () => {
    let j = this.state.thankyou
    j.push(JSON.parse(JSON.stringify(INIT_THANKYOU_TEMPLATE)))
    this.setState({ thankyou: j, saved: false })
  }

  isTemplateChanged = (index) => {
    const { thankyou, originalThankyou } = this.state
    // New template (not in original) is always considered changed
    if (index >= originalThankyou.length) return true
    // Compare JSON representations
    return JSON.stringify(thankyou[index]) !== JSON.stringify(originalThankyou[index])
  }

  toggleMessage = () => {
    this.setState({ showMessageModal: !this.state.showMessageModal })
  }

  toggleSchedule = () => {
    this.setState({ showScheduleModal: !this.state.showScheduleModal })
  }

  toggleResources = () => {
    this.setState({ showResourcesModal: !this.state.showResourcesModal })
  }

  setCategory = (selection) => {
    let _a = this.state.thankyou
    let _s = selection.value
    _a[this.state.selectedIndex].category = _s
    this.setState({ thankyou: _a, saved: false })
  }

  handleMessageBodyChange = (html) => {
    let j = this.state.thankyou
    if (!j[this.state.selectedIndex].message)
      j[this.state.selectedIndex].message = { htmlbody: '', subject: '' }
    j[this.state.selectedIndex].message.htmlbody = html
    delete j[this.state.selectedIndex].message.body
    this.setState({ thankyou: j, saved: false })
  }

  handleMessageSubjectChange = (e) => {
    let j = this.state.thankyou
    if (!j[this.state.selectedIndex].message)
      j[this.state.selectedIndex].message = { body: '', subject: '' }
    j[this.state.selectedIndex].message.subject = e.target.value
    this.setState({ thankyou: j, saved: false })
  }

  handleResourceChange = (e) => {
    let j = this.state.thankyou
    if (!j[this.state.selectedIndex].resources)
      j[this.state.selectedIndex].resources = []
    if (e.target.checked) {
      j[this.state.selectedIndex].resources.push(e.target.name)
    } else {
      j[this.state.selectedIndex].resources = j[
        this.state.selectedIndex
      ].resources.filter((x) => x !== e.target.name)
    }
    this.setState({ thankyou: j, saved: false })
  }

  onSave = (event) => {
    event.preventDefault()
    let _rows = this.state.thankyou.map((r) => {
      delete r.empty
      return r
    })
    this.props.firebase
      .addMessageTemplates({
        thankyou: _rows,
      })
      .then((dataSnapshot) => {
        this.setState({ saved: true })
      })
  }

  handleSelectEvent = (event) => {
    console.log(this.state.thankyou[this.state.selectedIndex].schedule)
    if (window.confirm('Poista ?')) {
      let j = this.state.thankyou
      j[this.state.selectedIndex].schedule = j[
        this.state.selectedIndex
      ].schedule.filter((e) => e.id !== event.id)
      this.setState({ thankyou: j, saved: false })
    }
  }

  handleSelectSlot = (slotInfo) => {
    console.log(slotInfo)
    let s = moment(slotInfo.start)
    let e = moment(slotInfo.end)
    if (e.diff(s, 'minutes') < 30) {
      e = moment(s).add(30, 'minutes')
    }
    let _ePlaceholder = {
      id: uuidv4(),
      start: s.format('HH:mm'),
      end: e.format('HH:mm'),
      day: slotInfo.resourceId,
    }
    let j = this.state.thankyou
    if (!j[this.state.selectedIndex].schedule) j[this.state.selectedIndex].schedule = []
    j[this.state.selectedIndex].schedule.push(_ePlaceholder)
    this.setState({ thankyou: j, saved: false })
  }

  moveEvent = ({ event, start, end, resourceId }) => {
    let j = this.state.thankyou
    j[this.state.selectedIndex].schedule = j[this.state.selectedIndex].schedule.map((e) => {
      if (e.id === event.id) {
        const _s = moment(start)
        const _e = moment(end)
        return { ...e, start: _s.format('HH:mm'), end: _e.format('HH:mm'), day: resourceId }
      } else {
        return e
      }
    })
    this.setState({ thankyou: j, saved: false })
  }

  resizeEvent = ({ event, start, end }) => {
    let j = this.state.thankyou
    j[this.state.selectedIndex].schedule = j[this.state.selectedIndex].schedule.map((e) => {
      if (e.id === event.id) {
        const _s = moment(start)
        const _e = moment(end)
        return { ...e, start: _s.format('HH:mm'), end: _e.format('HH:mm') }
      } else {
        return e
      }
    })
    this.setState({ thankyou: j, saved: false })
  }

  toggleDynamicHelp = () => {
    this.setState({ showDynamicHelp: !this.state.showDynamicHelp })
  }

  render() {
    if (!this.state.loadingCategories && !this.state.loadingTemplates) {
      return (
        <>
          <div className='content'>
            <Row>
              <Col md='12'>
                <Card>
                  <CardHeader>
                    <Row>
                      <Col lg='10' md='5' sm='5'>
                        <CardTitle tag='h5'>Kiitosviestit</CardTitle>
                      </Col>
                    </Row>
                  </CardHeader>
                  <CardBody>
                    {this.state.thankyou.map((c, i) => {
                      return (
                        <ThankYouMessageRow
                          key={i}
                          index={i}
                          template={c}
                          isDefault={c.default}
                          isChanged={this.isTemplateChanged(i)}
                          openEditMessageModal={() => {
                            this.setState({ selectedIndex: i })
                            this.toggleMessage()
                          }}
                          openEditScheduleModal={() => {
                            this.setState({ selectedIndex: i })
                            this.toggleSchedule()
                          }}
                          openEditResourcesModal={() => {
                            this.setState({ selectedIndex: i })
                            this.toggleResources()
                          }}
                          updateTemplateName={(e) => {
                            let j = this.state.thankyou
                            j[i].displayName = e.target.value
                            this.setState({ thankyou: j, saved: false })
                          }}
                          updateTemplateEnabled={(e) => {
                            let j = this.state.thankyou
                            j[i].enabled = e
                            this.setState({ thankyou: j, saved: false })
                          }}
                          updateBase={(base) => {
                            let j = this.state.thankyou
                            if (!j[i].timing) j[i].timing = {}
                            j[i].timing.base = base
                            // Reset delay to default value for new base type
                            j[i].timing.delay = getDefaultDelay(base)
                            this.setState({ thankyou: j, saved: false })
                          }}
                          updateDelay={(delay) => {
                            let j = this.state.thankyou
                            if (!j[i].timing) j[i].timing = {}
                            j[i].timing.delay = delay
                            this.setState({ thankyou: j, saved: false })
                          }}
                        />
                      )
                    })}
                    <Row>
                      <Col style={{ marginTop: '20px' }}>
                        <Button
                          className='btn-round btn-icon'
                          color='primary'
                          size='sm'
                          onClick={() => {
                            this.addThankYouRow()
                          }}
                        >
                          <i className='fa fa-plus' />
                        </Button>{' '}
                        Lisää kiitosviesti
                      </Col>
                    </Row>
                    <Row>
                      <Col className='ml-auto mr-auto' sm='12'>
                        <Button
                          disabled={this.state.saved}
                          color='primary'
                          onClick={this.onSave}
                        >
                          Tallenna muutokset
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            {/* Message modal */}
            <Modal
              isOpen={this.state.showMessageModal}
              toggle={this.toggleMessage}
              size='lg'
            >
              <ModalHeader toggle={this.toggleMessage}>Viesti</ModalHeader>
              <ModalBody>
              <div>
            <Label for='messagetemplates'>Dynaamiset kentät</Label>
            {this.state.showDynamicHelp ? <KeyboardArrowUpIcon onClick={this.toggleDynamicHelp} /> : <KeyboardArrowDownIcon onClick={this.toggleDynamicHelp} />}
          </div>
          {this.state.showDynamicHelp ? (
          <div>
            <Row>
              <Col>&#123;SUBJECT_SHORT&#125;</Col>
              <Col>Vakiomuotoinen otsikko</Col>
            </Row>
            <Row>
              <Col>&#123;PERSON_NAME&#125;</Col>
              <Col>Asiakkaan etu- ja sukunimi</Col>
            </Row>
            <Row>
              <Col>&#123;RESERVATION_DATE_TIME&#125;</Col>
              <Col>Varauksen päivä ja aika</Col>
            </Row>
            <Row>
              <Col>&#123;RESERVATION_VENUE&#125;</Col>
              <Col>Paikka</Col>
            </Row>
            <Row>
              <Col>&#123;RESERVATION_COURT&#125;</Col>
              <Col>Kenttä</Col>
            </Row>
            <Row>
              <Col>&#123;RESERVATION_SPORT&#125;</Col>
              <Col>Kategoria / laji</Col>
            </Row>
            <Row>
              <Col>&#123;RESERVATION_PRICE&#125;</Col>
              <Col>Hinta</Col>
            </Row>
            <Row>
              <Col>&#123;RESERVATION_CODE&#125;</Col>
              <Col>Sisäänpääsykoodi</Col>
            </Row>
            <Row>
              <Col>&#123;RESERVATION_EXTRAS&#125;</Col>
              <Col>Lisätuotteiden vahvistustekstit</Col>
            </Row>
          </div>
            ): null }


                <FormGroup>
                  <Label for='messageSubject'>Otsikko</Label>
                  <Input
                    id='messageSubject'
                    value={
                      this.state.thankyou?.[this.state.selectedIndex]
                        ?.message.subject
                        ? this.state.thankyou[this.state.selectedIndex]
                            ?.message.subject
                        : ''
                    }
                    name='messageSubject'
                    onChange={this.handleMessageSubjectChange}
                  />
                </FormGroup>
                <TinyEditor
                  value={
                    this.state.thankyou?.[this.state.selectedIndex]
                      ?.message.htmlbody
                      ? this.state.thankyou[this.state.selectedIndex]
                          ?.message.htmlbody
                      : this.state.thankyou?.[this.state.selectedIndex]
                          ?.message.body
                      ? this.state.thankyou[
                          this.state.selectedIndex
                        ]?.message.body.replace(/\n/g, '<br/>')
                      : ''
                  }
                  onChange={this.handleMessageBodyChange}
                  onClose={this.handleMessageBodyChange}
                  disableSave={true}
                />
              </ModalBody>
              <ModalFooter>
                <Button
                  color='primary'
                  onClick={() => {
                    this.toggleMessage()
                  }}
                >
                  Sulje
                </Button>
              </ModalFooter>
            </Modal>
            {/* Schedule modal */}
            <Modal
              isOpen={this.state.showScheduleModal}
              toggle={this.toggleSchedule}
              size='xl'
            >
              <ModalHeader toggle={this.toggleSchedule}>Aikataulu</ModalHeader>
              <ModalBody>
                <Row>
                  <Col md='12'>
                    <DnDCalendar
                      style={{ height: '85vh' }}
                      ref={this.myRef}
                      selectable
                      localizer={localizer}
                      events={formatSchedule(this.state.thankyou?.[this.state.selectedIndex]?.schedule || [])}
                      views={['day']}
                      step={30}
                      min={new Date(1970, 0, 1, 23, 0, 0)}
                      defaultView={Views.DAY}
                      scrollToTime={new Date(1970, 0, 1, 12)}
                      defaultDate={new Date(1970, 0, 1, 12)}
                      onSelectEvent={(event) => this.handleSelectEvent(event)}
                      onSelectSlot={this.handleSelectSlot}
                      components={{ toolbar: PricingToolBar }}
                      resources={weekdaysAsResources}
                      resourceIdAccessor='resourceId'
                      resourceTitleAccessor='resourceTitle'
                      draggableAccessor={(event) => true}
                      onEventDrop={this.moveEvent}
                      onEventResize={this.resizeEvent}
                      resizable
                    />
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button
                  color='primary'
                  onClick={() => {
                    this.toggleSchedule()
                  }}
                >
                  Sulje
                </Button>
              </ModalFooter>
            </Modal>
            {/* Resources modal */}
            <Modal
              isOpen={this.state.showResourcesModal}
              toggle={this.toggleResources}
              size='lg'
            >
              <ModalHeader toggle={this.toggleResources}>Resurssit</ModalHeader>
              <ModalBody>
                <div>
                  <p>
                    Valitse kategoria tai yksittäiset resurssit. Jos yksikin
                    resurssi valittu, niin kategoriaa ei huomioida.
                  </p>
                  <div
                    style={{
                      display:
                        this.state.thankyou[this.state.selectedIndex]
                          ?.resources &&
                        this.state.thankyou[this.state.selectedIndex]
                          ?.resources.length > 0
                          ? 'none'
                          : 'block',
                    }}
                  >
                    <Autocomplete
                      id={'catlabel'}
                      options={this.state.categoryOptions}
                      getOptionLabel={(o) => o.label || ''}
                      isOptionEqualToValue={(o, v) => o.value === v.value}
                      disabled={
                        this.state.thankyou[this.state.selectedIndex]?.default ||
                        (this.state.thankyou[this.state.selectedIndex]?.resources &&
                          this.state.thankyou[this.state.selectedIndex]?.resources.length > 0)
                      }
                      value={
                        this.state.thankyou[this.state.selectedIndex]?.category
                          ? this.state.categoryOptions.find(
                              (x) => x.value === this.state.thankyou[this.state.selectedIndex].category
                            ) || null
                          : null
                      }
                      onChange={(_e, val) => this.setCategory(val || { value: 'all', label: 'Kaikki' })}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label='Kategoria' variant='standard' fullWidth />
                      )}
                    />
                  </div>
                </div>
                <div>
                  <FormControl component='fieldset'>
                    <FormLabel component='legend'>Valitse resurssit</FormLabel>
                    <MuiFormGroup>
                      {this.state.resources.map((r, i) => {
                        return (
                          <FormControlLabel
                            key={i}
                            control={
                              <Checkbox
                                checked={
                                  this.state.thankyou?.[
                                    this.state.selectedIndex
                                  ]?.resources
                                    ? this.state.thankyou?.[
                                        this.state.selectedIndex
                                      ]?.resources.includes(r.id)
                                    : false
                                }
                                name={r.id}
                                onChange={this.handleResourceChange}
                              />
                            }
                            label={r.displayName}
                          />
                        )
                      })}
                    </MuiFormGroup>
                  </FormControl>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color='primary'
                  onClick={() => {
                    this.toggleResources()
                  }}
                >
                  Sulje
                </Button>
              </ModalFooter>
            </Modal>
          </div>
        </>
      )
    } else {
      return <></>
    }
  }
}

export default ThankYouMessageTemps
